import { useState } from 'react';
import { AdminLayout } from '../../components/Layout/AdminLayout';
import { PageHeader } from '../../components/common';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import {
  Edit,
  Delete,
  MoreVert,
  Notifications,
  Send,
  CheckCircle,
  Schedule,
  Group,
  MarkEmailRead,
} from '@mui/icons-material';
import {
  useSuperadminNotifications,
  useDeleteSuperadminNotification,
  useSendNotification,
} from '../../hooks';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { useQueryClient } from '@tanstack/react-query';
import { colors } from '../../theme/theme';

export const NotificationsPage = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedNotification, setSelectedNotification] = useState<any>(null);

  const { notifications, isLoading, error } = useSuperadminNotifications(page + 1);
  const deleteNotification = useDeleteSuperadminNotification();
  const sendNotification = useSendNotification();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, notification: any) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedNotification(notification);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedNotification(null);
  };

  const handleDelete = async () => {
    if (!selectedNotification) return;

    if (window.confirm(`Are you sure you want to delete notification "${selectedNotification.title}"?`)) {
      try {
        await deleteNotification.mutateAsync(selectedNotification.id);
        showSnackbar('Notification deleted successfully', 'success');
        queryClient.invalidateQueries({ queryKey: ['superadmin', 'notifications'] });
        handleMenuClose();
      } catch (error) {
        showSnackbar('Failed to delete notification', 'error');
      }
    }
  };

  const handleSend = async () => {
    if (!selectedNotification) return;

    if (window.confirm(`Are you sure you want to send notification "${selectedNotification.title}"?`)) {
      try {
        await sendNotification.mutateAsync(selectedNotification.id);
        showSnackbar('Notification sent successfully', 'success');
        queryClient.invalidateQueries({ queryKey: ['superadmin', 'notifications'] });
        handleMenuClose();
      } catch (error) {
        showSnackbar('Failed to send notification', 'error');
      }
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'title',
      headerName: 'Title',
      flex: 1,
      minWidth: 220,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Notifications sx={{ fontSize: 18, color: colors.primary.main }} />
          <Box>
            <Box sx={{ fontWeight: 600 }}>{params.value}</Box>
            <Box sx={{ fontSize: '0.75rem', color: colors.grey[600] }}>
              {params.row.message?.substring(0, 50) || 'No message'}
              {params.row.message?.length > 50 ? '...' : ''}
            </Box>
          </Box>
        </Box>
      ),
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 120,
      renderCell: (params) => {
        const typeColors: any = {
          success: 'success',
          warning: 'warning',
          error: 'error',
          info: 'info',
        };
        return (
          <Chip
            label={params.value || 'info'}
            size="small"
            color={typeColors[params.value] || 'info'}
            sx={{ textTransform: 'capitalize' }}
          />
        );
      },
    },
    {
      field: 'target',
      headerName: 'Target',
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.value || 'All'}
          size="small"
          variant="outlined"
          color="secondary"
          sx={{ textTransform: 'capitalize' }}
        />
      ),
    },
    {
      field: 'recipients_count',
      headerName: 'Recipients',
      width: 110,
      valueGetter: (value) => value || 0,
      renderCell: (params) => (
        <Chip
          icon={<Group sx={{ fontSize: 14 }} />}
          label={params.value}
          size="small"
          color="info"
          variant="outlined"
        />
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 110,
      renderCell: (params) => {
        const statusColors: any = {
          sent: 'success',
          pending: 'warning',
          draft: 'default',
          failed: 'error',
        };
        const statusIcons: any = {
          sent: <MarkEmailRead sx={{ fontSize: 14 }} />,
          pending: <Schedule sx={{ fontSize: 14 }} />,
          draft: <Edit sx={{ fontSize: 14 }} />,
        };
        return (
          <Chip
            icon={statusIcons[params.value]}
            label={params.value || 'draft'}
            color={statusColors[params.value] || 'default'}
            size="small"
            sx={{ textTransform: 'capitalize' }}
          />
        );
      },
    },
    {
      field: 'sent_at',
      headerName: 'Sent',
      width: 150,
      valueGetter: (value) => {
        if (!value) return 'Not sent';
        const date = new Date(value);
        return date.toLocaleString();
      },
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 120,
      valueGetter: (value) => (value ? new Date(value).toLocaleDateString() : 'N/A'),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 80,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: (params) => (
        <IconButton size="small" onClick={(e) => handleMenuOpen(e, params.row)}>
          <MoreVert />
        </IconButton>
      ),
    },
  ];

  return (
    <AdminLayout>
      <Box>
        <PageHeader
          title="Notifications"
          subtitle="Manage system notifications and announcements"
          breadcrumbs={[
            { label: 'Dashboard', path: '/superadmin/dashboard' },
            { label: 'System' },
            { label: 'Notifications' },
          ]}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load notifications. Please try again.
          </Alert>
        )}

        {/* Summary Cards */}
        {!isLoading && notifications && (
          <Grid container spacing={2.5} mb={3}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card
                elevation={0}
                sx={{
                  background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.primary.dark} 100%)`,
                  color: 'white',
                  borderRadius: 2.5,
                  border: `1px solid ${colors.primary.dark}`,
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 24px -10px ${colors.primary.main}80`,
                  },
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ opacity: 0.95, fontSize: '0.875rem', fontWeight: 500 }}>Total Notifications</Box>
                    <Notifications sx={{ fontSize: 24, opacity: 0.9 }} />
                  </Box>
                  <Box sx={{ fontWeight: 700, fontSize: '2rem', mb: 0.5 }}>
                    {notifications.meta?.total?.toLocaleString() || notifications.data?.length || 0}
                  </Box>
                  <Box sx={{ opacity: 0.8, fontSize: '0.75rem' }}>All notifications</Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card
                elevation={0}
                sx={{
                  background: `linear-gradient(135deg, ${colors.success.main} 0%, ${colors.success.dark} 100%)`,
                  color: 'white',
                  borderRadius: 2.5,
                  border: `1px solid ${colors.success.dark}`,
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 24px -10px ${colors.success.main}80`,
                  },
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ opacity: 0.95, fontSize: '0.875rem', fontWeight: 500 }}>Sent</Box>
                    <CheckCircle sx={{ fontSize: 24, opacity: 0.9 }} />
                  </Box>
                  <Box sx={{ fontWeight: 700, fontSize: '2rem', mb: 0.5 }}>
                    {notifications.data?.filter((n: any) => n.status === 'sent').length || 0}
                  </Box>
                  <Box sx={{ opacity: 0.8, fontSize: '0.75rem' }}>Successfully sent</Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card
                elevation={0}
                sx={{
                  background: `linear-gradient(135deg, ${colors.warning.main} 0%, ${colors.warning.dark} 100%)`,
                  color: 'white',
                  borderRadius: 2.5,
                  border: `1px solid ${colors.warning.dark}`,
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 24px -10px ${colors.warning.main}80`,
                  },
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ opacity: 0.95, fontSize: '0.875rem', fontWeight: 500 }}>Pending</Box>
                    <Schedule sx={{ fontSize: 24, opacity: 0.9 }} />
                  </Box>
                  <Box sx={{ fontWeight: 700, fontSize: '2rem', mb: 0.5 }}>
                    {notifications.data?.filter((n: any) => n.status === 'pending' || n.status === 'draft').length || 0}
                  </Box>
                  <Box sx={{ opacity: 0.8, fontSize: '0.75rem' }}>Awaiting send</Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card
                elevation={0}
                sx={{
                  background: `linear-gradient(135deg, ${colors.info.main} 0%, ${colors.info.dark} 100%)`,
                  color: 'white',
                  borderRadius: 2.5,
                  border: `1px solid ${colors.info.dark}`,
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 24px -10px ${colors.info.main}80`,
                  },
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ opacity: 0.95, fontSize: '0.875rem', fontWeight: 500 }}>Showing</Box>
                    <Group sx={{ fontSize: 24, opacity: 0.9 }} />
                  </Box>
                  <Box sx={{ fontWeight: 700, fontSize: '2rem', mb: 0.5 }}>
                    {notifications.data?.length || 0}
                  </Box>
                  <Box sx={{ opacity: 0.8, fontSize: '0.75rem' }}>On current page</Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        <Card
          elevation={0}
          sx={{
            borderRadius: 2.5,
            overflow: 'hidden',
            border: `1px solid ${colors.grey[200]}`,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
          }}
        >
          <CardContent sx={{ p: 0 }}>
            {isLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" py={8}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ width: '100%' }}>
                <DataGrid
                  rows={notifications?.data || []}
                  columns={columns}
                  pageSizeOptions={[10, 25, 50, 100]}
                  paginationModel={{ page, pageSize }}
                  onPaginationModelChange={(model) => {
                    setPage(model.page);
                    setPageSize(model.pageSize);
                  }}
                  rowCount={notifications?.meta?.total || notifications?.data?.length || 0}
                  paginationMode={notifications?.meta?.total ? 'server' : 'client'}
                  disableRowSelectionOnClick
                  autoHeight
                  slots={{ toolbar: GridToolbar }}
                  slotProps={{
                    toolbar: {
                      showQuickFilter: true,
                      quickFilterProps: { debounceMs: 500 },
                    },
                  }}
                  sx={{
                    border: 'none',
                    '& .MuiDataGrid-toolbarContainer': {
                      padding: '16px 20px',
                      borderBottom: `1px solid ${colors.grey[200]}`,
                      backgroundColor: colors.grey[50],
                    },
                    '& .MuiDataGrid-cell': {
                      borderBottom: `1px solid ${colors.grey[100]}`,
                      fontSize: '0.875rem',
                      py: 1.5,
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: colors.grey[50],
                      borderBottom: `2px solid ${colors.grey[300]}`,
                      fontSize: '0.8125rem',
                      fontWeight: 600,
                      color: colors.grey[700],
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    },
                    '& .MuiDataGrid-row': {
                      transition: 'background-color 0.15s ease',
                      '&:hover': {
                        backgroundColor: `${colors.primary.main}08`,
                        cursor: 'pointer',
                      },
                      '&:nth-of-type(even)': {
                        backgroundColor: 'rgba(0, 0, 0, 0.02)',
                      },
                    },
                    '& .MuiDataGrid-footerContainer': {
                      borderTop: `2px solid ${colors.grey[200]}`,
                      backgroundColor: colors.grey[50],
                      minHeight: '56px',
                    },
                  }}
                />
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Actions Menu */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          {selectedNotification?.status === 'draft' && (
            <MenuItem onClick={handleSend} sx={{ color: colors.success.main }}>
              <ListItemIcon>
                <Send fontSize="small" sx={{ color: colors.success.main }} />
              </ListItemIcon>
              <ListItemText>Send Notification</ListItemText>
            </MenuItem>
          )}
          <MenuItem onClick={handleMenuClose}>
            <ListItemIcon>
              <Edit fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit Notification</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleDelete} sx={{ color: colors.error.main }}>
            <ListItemIcon>
              <Delete fontSize="small" sx={{ color: colors.error.main }} />
            </ListItemIcon>
            <ListItemText>Delete Notification</ListItemText>
          </MenuItem>
        </Menu>
      </Box>
    </AdminLayout>
  );
};
